/**
 * Mega menu behaviour.
 *
 * Desktop (>=750px): pointer hover and click both open a panel; only one panel
 * is open at a time. Escape and focus leaving the menu close it.
 * Mobile (<750px): click toggles an accordion; panels stack and stay
 * independent so a shopper can open one section at a time.
 */
(() => {
  const DESKTOP = '(min-width: 900px)';

  /* Grace period before a panel closes on mouseleave. The panel is offset
     below the header row, so travelling from the link down to it crosses a
     gap that belongs to neither. A bridge in CSS covers the straight path;
     this covers diagonal ones, and lets a shopper clip a corner without the
     menu vanishing under the cursor. */
  const CLOSE_DELAY = 180;

  class MegaMenu {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('.fw-mega__item--has-panel'));
      this.mq = window.matchMedia(DESKTOP);
      this.bind();
    }

    get isDesktop() {
      return this.mq.matches;
    }

    bind() {
      this.items.forEach((item) => {
        const trigger = item.querySelector('.fw-mega__trigger');
        const panel = item.querySelector('.fw-mega__panel');
        if (!trigger || !panel) return;

        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          const open = item.dataset.open === 'true';
          if (this.isDesktop) this.closeAll();
          this.toggle(item, !open);
        });

        item.addEventListener('mouseenter', () => {
          if (!this.isDesktop) return;
          this.cancelClose();
          this.closeAll();
          this.toggle(item, true);
        });

        item.addEventListener('mouseleave', () => {
          if (!this.isDesktop) return;
          this.scheduleClose(item);
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAll();
      });

      // Close when focus or a click lands outside the menu (desktop only —
      // on mobile the accordion should stay open while browsing the drawer).
      document.addEventListener('focusin', (event) => {
        if (this.isDesktop && !this.root.contains(event.target)) this.closeAll();
      });

      document.addEventListener('click', (event) => {
        if (this.isDesktop && !this.root.contains(event.target)) this.closeAll();
      });
    }

    cancelClose() {
      if (!this.closeTimer) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    scheduleClose(item) {
      this.cancelClose();
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        this.toggle(item, false);
      }, CLOSE_DELAY);
    }

    toggle(item, open) {
      const trigger = item.querySelector('.fw-mega__trigger');
      const panel = item.querySelector('.fw-mega__panel');
      item.dataset.open = open ? 'true' : 'false';
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
    }

    closeAll() {
      this.cancelClose();
      this.items.forEach((item) => this.toggle(item, false));
    }
  }

  const init = () => {
    document.querySelectorAll('.fw-mega').forEach((root) => {
      if (root.dataset.fwMegaReady === 'true') return;
      root.dataset.fwMegaReady = 'true';
      new MegaMenu(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The header re-renders on section updates in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
